import React, { useEffect, useState } from "react";
import AdminLayout from "@/components/AdminLayout";
import { EngagementSummary } from "@/components/EngagementSummary";
import { ManifestoAnnounceCard } from "@/components/ManifestoAnnounceCard";
import { EmailComposerCard } from "@/components/EmailComposerCard";
import { api } from "@/lib/api";
import { Link } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { BookOpen, Users, FileText, Sparkles, ArrowUpRight, Plus } from "lucide-react";

export default function AdminDashboard() {
  const [courses, setCourses] = useState([]);
  const [learners, setLearners] = useState([]);

  useEffect(() => {
    (async () => {
      const [c, l] = await Promise.all([api.get("/courses"), api.get("/learners")]);
      setCourses(c.data);
      setLearners(l.data);
    })();
  }, []);

  const publishedCount = courses.filter((c) => c.published).length;

  const stats = [
    { label: "Total Courses", value: courses.length, icon: BookOpen, accent: "text-blue-900 bg-blue-50" },
    { label: "Published", value: publishedCount, icon: Sparkles, accent: "text-amber-700 bg-amber-50" },
    { label: "Learners", value: learners.length, icon: Users, accent: "text-emerald-700 bg-emerald-50" },
    { label: "Drafts", value: courses.length - publishedCount, icon: FileText, accent: "text-slate-700 bg-slate-100" },
  ];

  return (
    <AdminLayout>
      <div className="px-10 py-10 max-w-7xl">
        <header className="flex items-end justify-between mb-10">
          <div>
            <div className="text-xs uppercase tracking-[0.2em] text-slate-500 mb-2">Overview</div>
            <h1 className="text-4xl font-display font-bold text-slate-900 tracking-tight">Learning Studio</h1>
            <p className="text-slate-500 mt-2 max-w-xl">
              Build interactive learning modules from any document — powered by Claude, GPT, and Gemini.
            </p>
          </div>
          <Link to="/admin/courses">
            <Button data-testid="create-course-cta" className="bg-blue-900 hover:bg-blue-800 text-white">
              <Plus size={16} className="mr-1" /> New Course
            </Button>
          </Link>
        </header>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-10">
          {stats.map((s) => {
            const Icon = s.icon;
            return (
              <Card key={s.label} className="p-5 border-slate-200 hover:border-slate-300 transition-colors" data-testid={`stat-${s.label.toLowerCase().replace(/\s/g,'-')}`}>
                <div className={`inline-flex p-2 rounded-md ${s.accent} mb-3`}>
                  <Icon size={18} strokeWidth={1.7} />
                </div>
                <div className="text-3xl font-display font-bold text-slate-900">{s.value}</div>
                <div className="text-xs text-slate-500 mt-1 uppercase tracking-wide">{s.label}</div>
              </Card>
            );
          })}
        </div>

        <ManifestoAnnounceCard employeeCount={learners.filter((l) => (l.audience || "employee") !== "dealer").length} />

        <EmailComposerCard
          employeeCount={learners.filter((l) => (l.audience || "employee") !== "dealer").length}
          dealerCount={learners.filter((l) => l.audience === "dealer").length}
        />

        <EngagementSummary />

        <section>
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-display font-semibold text-slate-900">Recent Courses</h2>
            <Link to="/admin/courses" className="text-sm text-blue-900 hover:text-blue-700 flex items-center gap-1">
              View all <ArrowUpRight size={14} />
            </Link>
          </div>
          {courses.length === 0 ? (
            <Card className="p-12 text-center border-dashed">
              <BookOpen size={36} className="mx-auto text-slate-300 mb-3" strokeWidth={1.3} />
              <div className="text-slate-700 font-medium">No courses yet</div>
              <div className="text-sm text-slate-500 mt-1">Create your first course to get started.</div>
              <Link to="/admin/courses">
                <Button className="mt-4 bg-blue-900 hover:bg-blue-800 text-white">
                  <Plus size={16} className="mr-1" /> Create course
                </Button>
              </Link>
            </Card>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {courses.slice(0, 6).map((c) => (
                <Link key={c.id} to={`/admin/courses/${c.id}`} className="block group">
                  <Card className="p-5 border-slate-200 group-hover:border-blue-900/40 group-hover:shadow-md transition-all h-full">
                    <div className="flex items-start justify-between mb-3">
                      <span className={`text-[10px] uppercase tracking-wider font-semibold px-2 py-0.5 rounded ${c.published ? "bg-emerald-100 text-emerald-800" : "bg-slate-100 text-slate-600"}`}>
                        {c.published ? "Published" : "Draft"}
                      </span>
                      <ArrowUpRight size={16} className="text-slate-400 group-hover:text-blue-900 transition-colors" />
                    </div>
                    <h3 className="font-display font-semibold text-slate-900 mb-1 line-clamp-2">{c.title}</h3>
                    <p className="text-sm text-slate-500 line-clamp-2">{c.description || "—"}</p>
                  </Card>
                </Link>
              ))}
            </div>
          )}
        </section>
      </div>
    </AdminLayout>
  );
}
